/**
 * Parsing utilities for time / date strings in the source data.
 */
const TIME_PATTERN = /^\s*(\d{1,2}):(\d{2})\s*(AM|PM)\s*$/i;
/** Parse a single time-of-day string like "8:30 AM" → minutes after midnight. */
export function parseTimeOfDay(input) {
    const match = TIME_PATTERN.exec(input);
    if (!match)
        return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    const ampm = match[3].toUpperCase();
    if (!Number.isFinite(hours) || !Number.isFinite(minutes))
        return null;
    if (minutes < 0 || minutes > 59)
        return null;
    if (hours < 1 || hours > 12)
        return null;
    const base = (hours % 12) * 60 + minutes;
    return ampm === 'PM' ? base + 12 * 60 : base;
}
/** Parse a Shift cell like "8:30 AM to 12:00 PM" or "All Day". */
export function parseShiftTime(raw) {
    const text = (raw ?? '').trim();
    if (!text)
        return { allDay: false, startMin: null, endMin: null };
    if (/^all\s*day$/i.test(text)) {
        return { allDay: true, startMin: null, endMin: null };
    }
    const parts = text.split(/\s+to\s+/i);
    if (parts.length !== 2) {
        return { allDay: false, startMin: null, endMin: null };
    }
    const startMin = parseTimeOfDay(parts[0]);
    const endMin = parseTimeOfDay(parts[1]);
    if (startMin == null || endMin == null) {
        return { allDay: false, startMin: null, endMin: null };
    }
    return { allDay: false, startMin, endMin };
}
const RIDE_TIME_PATTERN = /^\s*([A-Za-z]+),\s*([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4}),\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)\s*$/;
const MONTH_INDEX = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    sept: 8,
    oct: 9,
    nov: 10,
    dec: 11,
};
/** Return the English weekday for an ISO calendar date ("YYYY-MM-DD"). */
export function weekdayNameFromIsoDate(iso) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
    if (!match)
        return null;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    if (Number.isNaN(date.getTime()))
        return null;
    return ([
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ][date.getUTCDay()] ?? null);
}
/** Parse a ShowConnect ride-time string like "Sat, Oct 4, 2025, 3:47 PM". */
export function parseRideTime(input) {
    if (!input)
        return null;
    const match = RIDE_TIME_PATTERN.exec(input);
    if (!match)
        return null;
    const monthRaw = match[2].toLowerCase();
    const month = MONTH_INDEX[monthRaw];
    if (month == null)
        return null;
    const day = Number(match[3]);
    const year = Number(match[4]);
    const hours12 = Number(match[5]);
    const minutes = Number(match[6]);
    const ampm = match[7].toUpperCase();
    if (!Number.isFinite(day) || !Number.isFinite(year))
        return null;
    if (day < 1 || day > 31)
        return null;
    if (hours12 < 1 || hours12 > 12)
        return null;
    if (minutes < 0 || minutes > 59)
        return null;
    const hours24 = (hours12 % 12) + (ampm === 'PM' ? 12 : 0);
    const date = `${year.toString().padStart(4, '0')}-${(month + 1)
        .toString()
        .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    const time = `${hours24.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    return { date, timeMin: hours24 * 60 + minutes, iso: `${date}T${time}` };
}
/** Format minutes-after-midnight back to "8:30 AM" for display. */
export function formatTimeOfDay(min) {
    if (min == null || !Number.isFinite(min))
        return '';
    const total = ((min % (24 * 60)) + 24 * 60) % (24 * 60);
    const hours24 = Math.floor(total / 60);
    const minutes = total % 60;
    const ampm = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
}
/** Format a parsed shift back to "8:30 AM to 12:00 PM" / "All Day" / "". */
export function formatShiftTime(parsed) {
    if (parsed.allDay)
        return 'All Day';
    if (parsed.startMin == null || parsed.endMin == null)
        return '';
    return `${formatTimeOfDay(parsed.startMin)} to ${formatTimeOfDay(parsed.endMin)}`;
}
//# sourceMappingURL=parsing.js.map